import React from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlaceIcon from "@mui/icons-material/Place";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AwardBadge } from "../../utils/awardIcons";
import { ClassificationIcon } from "../../utils/classificationIcons";
import { cropImageStyle } from "../../backend/companies/utils/companyUtils";
import { useI18n } from "../../../i18n/I18nContext";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function resolveImg(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value)) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

function localized(item, field, lang) {
    return item?.[`${field}_${lang}`] || item?.[`${field}_de`] || item?.name || "";
}

function formatCategory(item, lang) {
    if (!item) return "";
    if (typeof item === "string") return item;
    return localized(item, "label", lang);
}

function formatClassification(item, lang) {
    if (!item) return "";
    const label = localized(item, "label", lang);
    const value = item.value;
    const hasValue = value !== undefined && value !== null && String(value).trim() !== "";
    if (item.value_type === "boolean") return label;
    if (!hasValue) return label;
    return `${value} ${label}${item.is_superior ? " Superior" : ""}`.trim();
}

function LogoBadge({ logo, lang }) {
    const label = localized(logo, "label", lang);
    if (!label) return null;
    const url = resolveImg(logo.url);

    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                minHeight: 24,
                color: "var(--domain-text)",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
            }}
        >
            {url ? (
                <Box component="img" src={url} alt="" sx={{ width: 20, height: 20, objectFit: "contain", borderRadius: "50%" }} />
            ) : null}
            <Box component="span">{label}</Box>
        </Box>
    );
}

function MemberImage({ row, newLabel }) {
    const [broken, setBroken] = React.useState(false);
    const imgUrl = resolveImg(row.start_image_url);
    const showImage = imgUrl && !broken;

    return (
        <Box
            sx={{
                position: "relative",
                height: { xs: 168, md: 174 },
                flex: "0 0 auto",
                bgcolor: "#dbe2ea",
                overflow: "hidden",
                borderBottom: "1px solid #edf0f4",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(135deg, #eef2f6 0%, #cfd8e3 100%)",
                }}
            />
            {showImage ? (
                <Box
                    component="img"
                    src={imgUrl}
                    alt=""
                    onError={() => setBroken(true)}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        display: "block",
                        ...cropImageStyle(row.start_image_crop),
                    }}
                />
            ) : null}
            <Chip
                label={newLabel}
                size="small"
                sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    height: 23,
                    bgcolor: "#1d64d8",
                    color: "#fff",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                }}
            />
        </Box>
    );
}

export default function HomeLatestMembers({ limit = 4 }) {
    const navigate = useNavigate();
    const { t, lang } = useI18n();
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let active = true;

        axios
            .get(`${API_BASE}/api/filteredcompanies/latest`, { withCredentials: true })
            .then((res) => {
                if (active) setRows(Array.isArray(res.data) ? res.data.slice(0, limit) : []);
            })
            .catch(() => {
                if (active) setRows([]);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [limit]);

    if (!loading && rows.length === 0) return null;

    return (
        <Box
            sx={{
                position: "relative",
                left: "50%",
                right: "50%",
                width: "100vw",
                mx: "-50vw",
                mt: { xs: 6, md: 8 },
                mb: 0,
                py: { xs: 3.5, md: 4.5 },
                bgcolor: "background.paper",
                borderTop: "1px solid #e5e7eb",
                borderBottom: "1px solid #e5e7eb",
            }}
        >
            <Box sx={{ maxWidth: 1540, mx: "auto", px: { xs: 2, md: 4 } }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.75 }}>
                    <Typography component="h2" sx={{ fontSize: { xs: "1.35rem", md: "1.75rem" }, fontWeight: 800, color: "var(--domain-text)" }}>
                        {t("home.latestMembers.title")}
                    </Typography>
                    <Button
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        onClick={() => navigate("/neueste-mitglieder")}
                        sx={{ color: "var(--domain-text)", fontWeight: 700, textTransform: "none" }}
                    >
                        {t("home.latestMembers.viewAll")}
                    </Button>
                </Box>

                {loading ? (
                    <Paper variant="outlined" sx={{ py: 5, textAlign: "center", borderRadius: 1 }}>
                        <CircularProgress sx={{ color: "var(--domain-primary)" }} />
                    </Paper>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(4, minmax(0, 1fr))",
                            },
                            gap: 2.5,
                        }}
                    >
                        {rows.map((row) => {
                            const address = [
                                row.street,
                                [row.zip, row.city].filter(Boolean).join(" "),
                            ].filter(Boolean).join(", ");
                            const categories = Array.isArray(row.categories) ? row.categories : [];
                            const primaryClassification = formatClassification(row.primary_classification, lang);
                            const logos = Array.isArray(row.logos) ? row.logos : [];
                            const awards = Array.isArray(row.awards) ? row.awards : [];
                            const detailPath = `/companies/${row.slug || row.id}`;

                            return (
                                <Paper
                                    key={row.id}
                                    component="button"
                                    type="button"
                                    onClick={() => navigate(detailPath)}
                                    elevation={0}
                                    sx={{
                                        p: 0,
                                        textAlign: "left",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        bgcolor: "background.paper",
                                        cursor: "pointer",
                                        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            borderColor: "var(--domain-primary)",
                                            boxShadow: "0 14px 32px rgba(15,23,42,0.10)",
                                        },
                                    }}
                                >
                                    <MemberImage row={row} newLabel={t("home.latestMembers.newBadge")} />

                                    <Box sx={{ p: 2, flex: "1 1 auto" }}>
                                        <Typography sx={{ color: "var(--domain-text)", fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.2, mb: 1 }}>
                                            {row.name}
                                        </Typography>

                                        {address && (
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1.3 }}>
                                                <PlaceIcon sx={{ color: "var(--domain-primary)", fontSize: 18, mt: "1px", flexShrink: 0 }} />
                                                <Typography sx={{ color: "var(--domain-text-secondary)", fontSize: "0.9rem", lineHeight: 1.35 }}>
                                                    {address}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.15 }}>
                                            {categories.slice(0, 3).map((cat, index) => (
                                                <Chip key={`${formatCategory(cat, lang)}-${index}`} label={formatCategory(cat, lang)} size="small" sx={{ height: 24, bgcolor: "var(--domain-background)" }} />
                                            ))}
                                            {primaryClassification && (
                                                <Chip
                                                    label={primaryClassification}
                                                    size="small"
                                                    variant="outlined"
                                                    icon={<ClassificationIcon iconName={row.primary_classification?.icon_name} />}
                                                    sx={{ height: 24, borderColor: "var(--domain-primary)", color: "var(--domain-primary-dark)", bgcolor: "#fff7ed" }}
                                                />
                                            )}
                                        </Stack>

                                        {(logos.length > 0 || awards.length > 0) && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {logos.slice(0, 3).map((logo) => (
                                                    <LogoBadge key={`logo-${logo.id}`} logo={logo} lang={lang} />
                                                ))}
                                                {awards.slice(0, 2).map((award) => (
                                                    <AwardBadge key={`award-${award.id}`} award={award} size={20} />
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
